// src/components/UserProfile.tsx
import { useAuth } from "@/context/AuthContext";
import { Menu } from "@headlessui/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ConfirmDialog } from "../common/ConfirmDialog";

const AvatarSkeleton = () => (
  <div className="flex-shrink-0 bg-gray-200 rounded-full w-[40px] h-[40px] animate-pulse" />
);

const ProfileDetailsSkeleton = () => (
  <div className="flex items-center space-x-3">
    <AvatarSkeleton />
    <div>
      <div className="bg-gray-200 mb-1 rounded w-24 h-4 animate-pulse" />
      <div className="bg-gray-200 rounded w-16 h-3 animate-pulse" />
    </div>
  </div>
);

const UserProfile = () => {
  const navigate = useNavigate();
  const { logout, userDetails, fetchUserDetails } = useAuth();
  const version = "1.0.28";
  const [isLoading, setIsLoading] = useState(false);
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        await fetchUserDetails();
      } catch (error) {
        console.error("Failed to fetch user details:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (!userDetails) {
      fetchData();
    }
  }, [fetchUserDetails, userDetails]);

  const handleLogoutClick = () => {
    setIsLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      setIsLogoutDialogOpen(false);
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  return (
    <div className="p-6">
      <Menu as="div" className="relative">
        <Menu.Button className="flex justify-center items-center w-full group">
          {isLoading || !userDetails ? (
            <AvatarSkeleton />
          ) : (
            <div className="flex flex-shrink-0 justify-center items-center bg-primary-700 rounded-full w-[40px] h-[40px] text-overline text-white">
              {userDetails.name?.charAt(0).toUpperCase()}
            </div>
          )}
        </Menu.Button>

        <Menu.Items
          transition
          className="bottom-0 left-[70px] md:left-[80px] absolute bg-white ring-opacity-5 shadow-[0_4px_8px_-3px_rgba(5,83,156,0.15)] mb-2 rounded-[10px] ring-1 ring-black w-[220px] focus:outline-none dark:bg-dark transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0"
        >
            <div className="px-4 py-3">
              {isLoading || !userDetails ? (
                <ProfileDetailsSkeleton />
              ) : (
                <div className="flex items-center space-x-3">
                  <div className="flex justify-center items-center bg-primary-700 rounded-full w-[40px] h-[40px] font-bold text-white text-xl ">
                    {userDetails.name?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-dark text-overline dark:text-gray-200">
                      {userDetails.name}
                    </p>
                    <p className="text-gray-500 text-xs dark:text-gray-200">
                      {userDetails.userType}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="">
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={() => navigate("/account")}
                    className={`${active ? "bg-gray-100" : ""
                      } flex items-center w-full px-4 py-2 text-overline text-dark dark:text-gray-200 dark:hover:bg-black`}
                  >
                    Account
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    className={`${active ? "bg-gray-100" : ""
                      } flex items-center w-full px-4 py-2 text-overline text-dark dark:text-gray-200  dark:hover:bg-black`}
                  >
                    Notifications
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    className={`${active ? "bg-gray-100" : ""
                      } flex items-center w-full px-4 py-2 text-overline text-dark dark:text-gray-200  dark:hover:bg-black`}
                  >
                    Help Center
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    onClick={handleLogoutClick}
                    className={`${active ? "bg-gray-100" : ""
                      } flex items-center w-full px-4 py-2 text-overline text-dark dark:text-gray-200  dark:hover:bg-black`}
                  >
                    Log Out
                  </button>
                )}
              </Menu.Item>
              <div className="border-gray-200 border-t" />
              <div className="flex justify-center px-4 py-2">
                <span className="text-dark text-xs dark:text-gray-200">V{version}</span>
              </div>
            </div>
          </Menu.Items>
      </Menu>
      <ConfirmDialog
        open={isLogoutDialogOpen}
        onClose={() => setIsLogoutDialogOpen(false)}
        onConfirm={handleLogoutConfirm}
        title="Logout"
        message="Are you sure you want to logout?"
        confirmText="Logout"
        cancelText="Cancel"
      />
    </div>
  );
};

export default UserProfile;
